import { useMemo } from "react";
import { filter, sortBy } from "lodash-es";
import {
  DataItem,
  SortType,
  MonthName,
  FilterType,
  Hemisphere,
  MONTH_NAMES,
  ASC_PRICE,
  DESC_PRICE,
  ASC_NAME,
  DESC_NAME,
  NORTHERN,
} from "../types";

interface UseDataFilteringProps {
  data: DataItem[];
  currentSort: SortType;
  currentMonth: MonthName | "AllMonths";
  currentFilter: FilterType;
  searchTerm: string;
  hemisphere: Hemisphere;
}

export const useDataFiltering = ({
  data,
  currentSort,
  currentMonth,
  currentFilter,
  searchTerm,
  hemisphere,
}: UseDataFilteringProps) => {
  const filteredData = useMemo(() => {
    let tempData = [...data];

    // Apply month filter
    if (currentMonth !== "AllMonths") {
      const monthIndex = MONTH_NAMES.indexOf(currentMonth as MonthName) + 1;
      tempData = filter(tempData, (item: DataItem) => {
        const startMonth1 =
          hemisphere === NORTHERN
            ? item.northStartMonth1
            : item.southStartMonth1;
        const endMonth1 =
          hemisphere === NORTHERN ? item.northEndMonth1 : item.southEndMonth1;
        const startMonth2 =
          hemisphere === NORTHERN
            ? item.northStartMonth2
            : item.southStartMonth2;
        const endMonth2 =
          hemisphere === NORTHERN ? item.northEndMonth2 : item.southEndMonth2;

        if (startMonth1 === -1) return true;
        if (startMonth1 <= monthIndex && endMonth1 >= monthIndex) return true;
        if (startMonth2 <= monthIndex && endMonth2 >= monthIndex) return true;
        if (
          startMonth1 >= monthIndex &&
          endMonth1 >= monthIndex &&
          endMonth1 <= startMonth1
        )
          return true;
        if (
          startMonth2 >= monthIndex &&
          endMonth2 >= monthIndex &&
          endMonth2 <= startMonth2
        )
          return true;

        return false;
      });
    }

    // Apply type filter
    if (currentFilter !== "All") {
      tempData = filter(
        tempData,
        (item: DataItem) =>
          item.type === currentFilter ||
          item.name.toLowerCase().includes(currentFilter.toLowerCase()) ||
          item.location.toLowerCase().includes(currentFilter.toLowerCase()),
      );
    }

    // Apply search term filter
    if (searchTerm) {
      tempData = filter(
        tempData,
        (item: DataItem) =>
          item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.type.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.location.toLowerCase().includes(searchTerm.toLowerCase()),
      );
    }

    // Sort data
    switch (currentSort) {
      case ASC_PRICE:
        return sortBy(tempData, "price");
      case DESC_PRICE:
        return sortBy(tempData, "price").reverse();
      case ASC_NAME:
        return sortBy(tempData, "name");
      case DESC_NAME:
        return sortBy(tempData, "name").reverse();
      default:
        return tempData;
    }
  }, [data, currentSort, currentMonth, currentFilter, searchTerm, hemisphere]);

  return { filteredData };
};
